"use client";

import type { PlainDate, ZonedDateTime } from "@sa-nu/shared-utils/date";
import { toPlainDateString } from "@sa-nu/shared-utils/date";

import type { StayTicketMasterReferenceInput } from "../../../../../__generated__/gql/graphql";
import { deriveConfirmedPrice } from "../_utils/derive-confirmed-price";

import {
  useRoomTypeAvailabilityByStayTicketMaster,
  type RoomTypeAvailabilityResult,
} from "./use-room-type-availability";

export type ConfirmedFeeOption = {
  /** 確定CI〜CO前夜の合計金額。fetch 未完了・失敗時は null */
  price: number | null;
  loading: boolean;
};

export type ConfirmedFeeOptions = {
  base: ConfirmedFeeOption;
  /** 取得に失敗した、またはテンプレートが使えないときは null。呼び出し元は base だけの表示にフォールバックする */
  noCancelOption: ConfirmedFeeOption | null;
};

function toConfirmedPrice(
  availability: RoomTypeAvailabilityResult,
  firstNight: PlainDate,
  lastNight: PlainDate,
): number | null {
  // deriveConfirmedPrice は欠けた夜を 0 円として足すため、nights が空になる取得中・失敗時や、
  // resolver がエラーにせず空で返すテンプレート無効・対象外の部屋タイプを通すと 0 円で押せるカードになる。
  if (
    availability.loading ||
    availability.error !== undefined ||
    availability.stayTicketMasterId === null ||
    availability.nights.length === 0
  ) {
    return null;
  }

  const nightPrices = Object.fromEntries(
    availability.nights.map((n) => [toPlainDateString(n.night), n.price]),
  );
  // 対象は必ず STAY_TICKET_MASTER_CONFIG 登録済みのため、確定金額の利用可否は true 固定でよい。
  return deriveConfirmedPrice(true, nightPrices, firstNight, lastNight);
}

/** カレンダー表示用の availability は base だけで noCancelOption の価格を持たないため、確定金額は両オプション分取り直す */
export function useConfirmedFeeOptions({
  roomTypeId,
  baseStayTicketMasterReference,
  noCancelOptionStayTicketMasterReference,
  evChargerAvailable,
  currentTime,
  firstNight,
  lastNight,
  skip,
}: {
  roomTypeId: string;
  baseStayTicketMasterReference: StayTicketMasterReferenceInput;
  noCancelOptionStayTicketMasterReference: StayTicketMasterReferenceInput;
  evChargerAvailable: boolean;
  currentTime: ZonedDateTime;
  firstNight: PlainDate | null;
  lastNight: PlainDate | null;
  /** true のとき両フックの fetch をスキップし、null を返す（CI/CO 未確定時も同様） */
  skip: boolean;
}): ConfirmedFeeOptions | null {
  const shouldSkip = skip || firstNight === null || lastNight === null;

  const baseAvailability = useRoomTypeAvailabilityByStayTicketMaster({
    roomTypeId,
    stayTicketMasterReference: baseStayTicketMasterReference,
    evChargerAvailable,
    currentTime,
    firstNight: firstNight ?? undefined,
    lastNight: lastNight ?? undefined,
    skip: shouldSkip,
  });
  const noCancelOptionAvailability = useRoomTypeAvailabilityByStayTicketMaster({
    roomTypeId,
    stayTicketMasterReference: noCancelOptionStayTicketMasterReference,
    evChargerAvailable,
    currentTime,
    firstNight: firstNight ?? undefined,
    lastNight: lastNight ?? undefined,
    skip: shouldSkip,
  });

  if (skip || firstNight === null || lastNight === null) return null;

  const noCancelOptionPrice = toConfirmedPrice(
    noCancelOptionAvailability,
    firstNight,
    lastNight,
  );
  // 取得を終えても金額が出ないのは失敗かテンプレートが使えない場合なので、base だけの表示に倒す。
  const isNoCancelOptionUnavailable =
    !noCancelOptionAvailability.loading && noCancelOptionPrice === null;

  return {
    base: {
      price: toConfirmedPrice(baseAvailability, firstNight, lastNight),
      loading: baseAvailability.loading,
    },
    noCancelOption: isNoCancelOptionUnavailable
      ? null
      : {
          price: noCancelOptionPrice,
          loading: noCancelOptionAvailability.loading,
        },
  };
}
